export type TranslationKey = string;

export interface LocaleConfig {
    [key: string]: string;
}

export const EN_US: LocaleConfig = {
    'ui.effects': 'Visual effects',
    'ui.reduceShake': 'No camera shake',
    'ui.reduceFlashes': 'Fewer flashes',
    'ui.enabled': 'On',
    'ui.disabled': 'Off',
    'chest.empty': "The container is empty.",
    'ui.next': "Next",
    'ui.back': "Back",
    'ui.inventory': "Inventory",
    'ui.sound': "Sound",
    'ui.stats': "Stats",
    'ui.language': "Language",
    'ui.wave': "Mutation {0}",
    'ui.experience': "XP",
    'ui.health': "HP",
    'ui.boss': "Boss",
    'ui.sound.sfx': "Effects",
    'ui.sound.music': "Music",
    'ui.consumables': "Supplies",
    'boss.iron_colossus.name': "Iron Croaker",
    'boss.void_tyrant.name': "Overgrown Croaker",
    'boss.dark_lord.name': "Mire Baron",
    // UI
    'ui.paused': 'PAUSED',
    'ui.weapons': "Weapons",
    'ui.passives': "Equipment",
    'ui.none': 'None',
    'ui.main_menu': 'MAIN MENU',
    'ui.loading': 'Loading...',
    'ui.resume': 'Resume',
    'ui.telegram_news': 'Follow Frognobyl news on Telegram',
    'ui.level': 'Lv.',
    'ui.damage': 'Damage',
    'ui.speed': 'Speed',
    'ui.max_hp': 'Max HP',
    'ui.cooldown': 'Cooldown',
    'ui.pickup': 'Pickup',
    'ui.inventory.weapons': "Weapons",
    'ui.inventory.passives': "Equipment",
    'ui.empty_slot': 'Empty',
    'ui.controls': 'WASD to Move | Auto-Shoot',
    'mobile.orientation.title': 'Rotate your device',
    'mobile.orientation.body': 'Rotate to landscape, then open the game fullscreen for stable touch controls.',
    'mobile.fullscreen.cta': 'Open fullscreen',
    'mobile.fullscreen.active': 'Fullscreen active',
    'mobile.fullscreen.unsupported': 'Fullscreen unavailable',
    'ui.sound.mute_on': 'MUTE: ON',
    'ui.sound.mute_off': 'MUTE: OFF',
    'ui.sound.volume': 'VOL: {0}%',
    'profile.records.title': 'Profile Records',
    'profile.records.best_time': 'Best time: {0}s',
    'profile.records.runs': 'Runs: {0} | Victories: {1}',
    'profile.run.time': 'Run time: {0}s',

    // Menu
    'menu.title': 'FROGNOBYL',
    'menu.subtitle': 'Mutant frogs invasion',
    'menu.play': 'PLAY',
    'menu.settings': 'Settings',
    'menu.language': 'Language',
    'menu.community': 'Community',
    'menu.close': 'Close',

    // Chest
    'chest.title': "SUPPLY CONTAINER",
    'chest.found': 'You found {0}!',
    'chest.you_found': 'You found:',
    'chest.collect': 'Collect',
    'chest.evolution': 'WEAPON EVOLUTION!',
    'chest.evolved_into': 'Evolved into:\n{0}!',

    // Warnings
    'warning.boss': 'WARNING:\nBOSS APPROACHING!',

    // Screens
    'screen.levelup.title': 'LEVEL UP!',
    'screen.gameover.title': "RUN OVER",
    'screen.gameover.score': 'Score: {0}',
    'screen.gameover.restart': 'Restart Game',
    'screen.victory.title': "SWAMP SURVIVOR",
    'screen.victory.subtitle': "Bosses defeated. Run complete.",
    'screen.victory.level_reached': 'Level Reached: {0}',
    'screen.victory.play_again': 'Play Again',

    // Weapons - Basic Shot
    'weapon.basic_shot.name': "Croak Cannon",
    'weapon.basic_shot.description.1': "1 projectile, 10 damage, 3 s cooldown. Targets the nearest enemy.",
    'weapon.basic_shot.description.2': "2 projectiles, 10 damage, 3 s cooldown.",
    'weapon.basic_shot.description.3': "2 projectiles, 15 damage, 2.7 s cooldown.",
    'weapon.basic_shot.description.4': "3 projectiles, 15 damage, 2.7 s cooldown.",
    'weapon.basic_shot.description.5': "3 projectiles, 20 damage, up to 2 targets each. 2.4 s cooldown.",

    // Weapons - Orbit Shield
    'weapon.orbit_shield.name': "Coil Carousel",
    'weapon.orbit_shield.description.1': "1 orbiting coil. 5 contact damage.",
    'weapon.orbit_shield.description.2': "2 orbiting coils. 5 contact damage.",
    'weapon.orbit_shield.description.3': "2 coils, 8 damage. Rotation speed 72 → 100.",
    'weapon.orbit_shield.description.4': "3 coils, 8 damage. Rotation speed 100 → 120.",
    'weapon.orbit_shield.description.5': "4 coils, 12 damage. Rotation speed 120 → 150.",

    // Weapons - Holy Wand
    'weapon.holy_wand.name': "Croak Accelerator",
    'weapon.holy_wand.description.1': "Evolved Croak Cannon: 4 projectiles, 20 damage, up to 5 targets each. 1.2 s cooldown.",

    // Weapons - Death Spiral
    'weapon.death_spiral.name': "Cleanup Rotor",
    'weapon.death_spiral.description.1': "Evolved Coil Carousel: 6 orbiting projectiles, 12 damage, 85 radius.",
    'weapon.area_pulse.name': "Thermocroak",
    'weapon.area_pulse.description.1': "14 damage, 80 radius. Burn: 2 HP/s for 1.5 s. 4.2 s cooldown.",
    'weapon.area_pulse.description.2': "17 damage, 92 radius. Burn: 2 HP/s for 1.5 s. 4 s cooldown.",
    'weapon.area_pulse.description.3': "20 damage, 105 radius. Burn: 4 HP/s for 2 s. 3.7 s cooldown.",
    'weapon.prism_beam.name': "Decon Beam",
    'weapon.prism_beam.description.1': "12 + 2 damage, 220 range. Crit: 12%. 3.4 s cooldown.",
    'weapon.prism_beam.description.2': "16 + 4 damage, 250 range. Crit: 12%. 3.1 s cooldown.",
    'weapon.prism_beam.description.3': "19 + 5 damage, 280 range. Crit: 20%. 2.8 s cooldown.",
    'weapon.boomerang_blade.name': "Rusty Boomerang",
    'weapon.boomerang_blade.description.1': "1 boomerang, 10 damage. Slows by 20% for 1.4 s. 2.9 s cooldown.",
    'weapon.boomerang_blade.description.2': "2 boomerangs, 13 damage. Return speed 260 → 300. 2.7 s cooldown.",
    'weapon.boomerang_blade.description.3': "16 damage, 340 return speed. Slows by 30% for 2 s. 2.5 s cooldown.",
    'weapon.chain_arc.name': "Arc Discharger",
    'weapon.chain_arc.description.1': "Up to 3 targets, 11 damage. 140 radius, 25% falloff per hop. 3.2 s cooldown.",
    'weapon.chain_arc.description.2': "Up to 4 targets, 14 damage. 160 radius, 20% falloff per hop. 3 s cooldown.",
    'weapon.chain_arc.description.3': "Up to 5 targets, 17 damage. 180 radius, 15% falloff, 18% crit. 2.7 s cooldown.",

    // Upgrades
    'upgrade.unlock': 'New! {0}',
    'upgrade.level': '{0} Level {1}',
    'upgrade.fallback.desc': 'Unlocks a new weapon.',
    'upgrade.max_level_reached': 'MAX',

    // Passive Items
    'passive.might.name': "Power Ration",
    'passive.might.description.1': "Damage +10%.",
    'passive.might.description.2': "Damage +20%.",
    'passive.might.description.3': "Damage +30%.",
    'passive.might.description.4': "Damage +40%.",
    'passive.might.description.5': "Damage +50%.",
    'passive.haste.name': "Overdrive Coil",
    'passive.haste.description.1': "Attack cooldown −5%.",
    'passive.haste.description.2': "Attack cooldown −10%.",
    'passive.haste.description.3': "Attack cooldown −15%.",
    'passive.haste.description.4': "Attack cooldown −20%.",
    'passive.haste.description.5': "Attack cooldown −25%.",
    'passive.armor.name': "Vital Reserve",
    'passive.armor.description.1': "Max HP 120. Heal by the increase in max HP.",
    'passive.armor.description.2': "Max HP 140. Heal by the increase in max HP.",
    'passive.armor.description.3': "Max HP 160. Heal by the increase in max HP.",
    'passive.armor.description.4': "Max HP 180. Heal by the increase in max HP.",
    'passive.armor.description.5': "Max HP 200. Heal by the increase in max HP.",
    'passive.speed.name': "Swamp Striders",
    'passive.speed.description.1': "Move speed +10%: 176.",
    'passive.speed.description.2': "Move speed +20%: 192.",
    'passive.speed.description.3': "Move speed +30%: 208.",
    'passive.speed.description.4': "Move speed +40%: 224.",
    'passive.speed.description.5': "Move speed +50%: 240.",
    'passive.magnet.name': "Sample Collector",
    'passive.magnet.description.1': "Pickup radius 120.",
    'passive.magnet.description.2': "Pickup radius 145.",
    'passive.magnet.description.3': "Pickup radius 170.",
    'passive.magnet.description.4': "Pickup radius 200.",
    'passive.magnet.description.5': "Pickup radius 235.",

    // Consumables
    'consumable.heal_20.title': "Emergency Ration",
    'consumable.heal_20.desc': "Restores up to 20 HP, without exceeding max HP.",
    'consumable.gold_25.title': "Gold Reserve",
    'consumable.gold_25.desc': "25 gold. Rewards disabled until the economy is available.",

    // Stat Boosts
    'upgrade.stat.heal_20.title': 'Health Potion',
    'upgrade.stat.heal_20.desc': 'Heal 20 HP',
    'upgrade.stat.might_10.title': 'Spinach',
    'upgrade.stat.might_10.desc': 'Damage +10%',
    'upgrade.stat.haste_10.title': 'Empty Tome',
    'upgrade.stat.haste_10.desc': 'Cooldown -10%',
    'upgrade.stat.speed_10.title': 'Sneakers',
    'upgrade.stat.speed_10.desc': 'Move Speed +10%'
};

export const RU_RU: LocaleConfig = {
    'ui.effects': 'Эффекты',
    'ui.reduceShake': 'Без тряски',
    'ui.reduceFlashes': 'Меньше вспышек',
    'ui.enabled': 'Вкл',
    'ui.disabled': 'Выкл',
    'chest.empty': "Контейнер пуст.",
    'ui.next': "Далее",
    'ui.back': "Назад",
    'ui.inventory': "Снаряжение",
    'ui.sound': "Звук",
    'ui.stats': "Характеристики",
    'ui.language': "Язык",
    'ui.wave': "Мутация {0}",
    'ui.experience': "Опыт",
    'ui.health': "ОЗ",
    'ui.boss': "Босс",
    'ui.sound.sfx': "Эффекты",
    'ui.sound.music': "Музыка",
    'ui.consumables': "Припасы",
    'boss.iron_colossus.name': "Железный Квак",
    'boss.void_tyrant.name': "Квак-переросток",
    'boss.dark_lord.name': "Барон Трясины",
    // UI
    'ui.paused': 'ПАУЗА',
    'ui.weapons': "Оружие",
    'ui.passives': "Снаряжение",
    'ui.none': 'Нет',
    'ui.main_menu': 'ГЛАВНОЕ МЕНЮ',
    'ui.loading': 'Загрузка...',
    'ui.resume': 'Продолжить',
    'ui.telegram_news': 'Подпишись на новости Лягушнобля в Telegram',
    'ui.level': 'Ур.',
    'ui.damage': 'Урон',
    'ui.speed': 'Скорость',
    'ui.max_hp': 'Макс. ОЗ',
    'ui.cooldown': 'Перезарядка',
    'ui.pickup': 'Подбор',
    'ui.inventory.weapons': "Оружие",
    'ui.inventory.passives': "Снаряжение",
    'ui.empty_slot': 'Пусто',
    'ui.controls': 'WASD - Движение | Авто-стрельба',
    'mobile.orientation.title': 'Поверните устройство',
    'mobile.orientation.body': 'Поверните в альбомный режим и откройте игру на весь экран для стабильного управления.',
    'mobile.fullscreen.cta': 'Открыть на весь экран',
    'mobile.fullscreen.active': 'Полный экран включен',
    'mobile.fullscreen.unsupported': 'Полный экран недоступен',
    'ui.sound.mute_on': 'ЗВУК: ВЫКЛ',
    'ui.sound.mute_off': 'ЗВУК: ВКЛ',
    'ui.sound.volume': 'ГРОМК.: {0}%',
    'profile.records.title': 'Рекорды профиля',
    'profile.records.best_time': 'Лучшее время: {0}с',
    'profile.records.runs': 'Забеги: {0} | Победы: {1}',
    'profile.run.time': 'Время забега: {0}с',

    // Menu
    'menu.title': 'ЛЯГУШНОБЛЬ',
    'menu.subtitle': 'Нашествие лягушек-мутантов',
    'menu.play': 'ИГРАТЬ',
    'menu.settings': 'Настройки',
    'menu.language': 'Язык',
    'menu.community': 'Сообщество',
    'menu.close': 'Закрыть',

    // Chest
    'chest.title': "КОНТЕЙНЕР СНАБЖЕНИЯ",
    'chest.found': 'Вы нашли {0}!',
    'chest.you_found': 'Вы нашли:',
    'chest.collect': 'Забрать',
    'chest.evolution': 'ЭВОЛЮЦИЯ ОРУЖИЯ!',
    'chest.evolved_into': 'Эволюционировало в:\n{0}!',

    // Warnings
    'warning.boss': 'ВНИМАНИЕ:\nПРИБЛИЖАЕТСЯ БОСС!',

    // Screens
    'screen.levelup.title': 'НОВЫЙ УРОВЕНЬ!',
    'screen.gameover.title': "ЗАБЕГ ОКОНЧЕН",
    'screen.gameover.score': 'Счет: {0}',
    'screen.gameover.restart': 'Играть снова',
    'screen.victory.title': "БОЛОТО ВЫДЕРЖАЛО",
    'screen.victory.subtitle': "Боссы повержены. Забег завершён.",
    'screen.victory.level_reached': 'Достигнут уровень: {0}',
    'screen.victory.play_again': 'Играть снова',

    // Weapons - Basic Shot
    'weapon.basic_shot.name': "Квакомёт",
    'weapon.basic_shot.description.1': "1 снаряд, урон 10, задержка 3 с. Цель — ближайший враг.",
    'weapon.basic_shot.description.2': "2 снаряда, урон 10, задержка 3 с.",
    'weapon.basic_shot.description.3': "2 снаряда, урон 15, задержка 2,7 с.",
    'weapon.basic_shot.description.4': "3 снаряда, урон 15, задержка 2,7 с.",
    'weapon.basic_shot.description.5': "3 снаряда, урон 20, до 2 целей каждым. Задержка 2,4 с.",

    // Weapons - Orbit Shield
    'weapon.orbit_shield.name': "Хоровод катушек",
    'weapon.orbit_shield.description.1': "1 катушка вокруг героя. Контактный урон 5.",
    'weapon.orbit_shield.description.2': "2 катушки вокруг героя. Контактный урон 5.",
    'weapon.orbit_shield.description.3': "2 катушки, урон 8. Скорость вращения 72 → 100.",
    'weapon.orbit_shield.description.4': "3 катушки, урон 8. Скорость вращения 100 → 120.",
    'weapon.orbit_shield.description.5': "4 катушки, урон 12. Скорость вращения 120 → 150.",

    // Weapons - Holy Wand
    'weapon.holy_wand.name': "Ква-ускоритель",
    'weapon.holy_wand.description.1': "Эволюция Квакомёта: 4 снаряда, урон 20, до 5 целей каждым. Задержка 1,2 с.",

    // Weapons - Death Spiral
    'weapon.death_spiral.name': "Ротор зачистки",
    'weapon.death_spiral.description.1': "Эволюция Хоровода катушек: 6 вращающихся снарядов, урон 12, радиус 85.",
    'weapon.area_pulse.name': "Термоквак",
    'weapon.area_pulse.description.1': "Урон 14, радиус 80. Горение: 2 ОЗ/с на 1,5 с. Задержка 4,2 с.",
    'weapon.area_pulse.description.2': "Урон 17, радиус 92. Горение: 2 ОЗ/с на 1,5 с. Задержка 4 с.",
    'weapon.area_pulse.description.3': "Урон 20, радиус 105. Горение: 4 ОЗ/с на 2 с. Задержка 3,7 с.",
    'weapon.prism_beam.name': "Луч санобработки",
    'weapon.prism_beam.description.1': "Урон 12 + 2, дальность 220. Крит: 12%. Задержка 3,4 с.",
    'weapon.prism_beam.description.2': "Урон 16 + 4, дальность 250. Крит: 12%. Задержка 3,1 с.",
    'weapon.prism_beam.description.3': "Урон 19 + 5, дальность 280. Крит: 20%. Задержка 2,8 с.",
    'weapon.boomerang_blade.name': "Ржавый бумеранг",
    'weapon.boomerang_blade.description.1': "1 бумеранг, урон 10. Замедление 20% на 1,4 с. Задержка 2,9 с.",
    'weapon.boomerang_blade.description.2': "2 бумеранга, урон 13. Возврат 260 → 300. Задержка 2,7 с.",
    'weapon.boomerang_blade.description.3': "Урон 16, возврат 340. Замедление 30% на 2 с. Задержка 2,5 с.",
    'weapon.chain_arc.name': "Разрядник",
    'weapon.chain_arc.description.1': "До 3 целей, урон 11. Радиус 140, ослабление за переход 25%. Задержка 3,2 с.",
    'weapon.chain_arc.description.2': "До 4 целей, урон 14. Радиус 160, ослабление за переход 20%. Задержка 3 с.",
    'weapon.chain_arc.description.3': "До 5 целей, урон 17. Радиус 180, ослабление 15%, крит 18%. Задержка 2,7 с.",

    // Upgrades
    'upgrade.unlock': 'Новое! {0}',
    'upgrade.level': '{0} Ур. {1}',
    'upgrade.fallback.desc': 'Открывает новое оружие.',
    'upgrade.max_level_reached': 'МАКС',

    // Passive Items
    'passive.might.name': "Усиленный паёк",
    'passive.might.description.1': "Урон +10%.",
    'passive.might.description.2': "Урон +20%.",
    'passive.might.description.3': "Урон +30%.",
    'passive.might.description.4': "Урон +40%.",
    'passive.might.description.5': "Урон +50%.",
    'passive.haste.name': "Разгонная катушка",
    'passive.haste.description.1': "Задержка атак −5%.",
    'passive.haste.description.2': "Задержка атак −10%.",
    'passive.haste.description.3': "Задержка атак −15%.",
    'passive.haste.description.4': "Задержка атак −20%.",
    'passive.haste.description.5': "Задержка атак −25%.",
    'passive.armor.name': "Запас живучести",
    'passive.armor.description.1': "Максимум 120 ОЗ. Лечение на прирост максимума.",
    'passive.armor.description.2': "Максимум 140 ОЗ. Лечение на прирост максимума.",
    'passive.armor.description.3': "Максимум 160 ОЗ. Лечение на прирост максимума.",
    'passive.armor.description.4': "Максимум 180 ОЗ. Лечение на прирост максимума.",
    'passive.armor.description.5': "Максимум 200 ОЗ. Лечение на прирост максимума.",
    'passive.speed.name': "Болотоступы",
    'passive.speed.description.1': "Скорость движения +10%: 176.",
    'passive.speed.description.2': "Скорость движения +20%: 192.",
    'passive.speed.description.3': "Скорость движения +30%: 208.",
    'passive.speed.description.4': "Скорость движения +40%: 224.",
    'passive.speed.description.5': "Скорость движения +50%: 240.",
    'passive.magnet.name': "Сборщик проб",
    'passive.magnet.description.1': "Радиус подбора 120.",
    'passive.magnet.description.2': "Радиус подбора 145.",
    'passive.magnet.description.3': "Радиус подбора 170.",
    'passive.magnet.description.4': "Радиус подбора 200.",
    'passive.magnet.description.5': "Радиус подбора 235.",

    // Consumables
    'consumable.heal_20.title': "Аварийный паёк",
    'consumable.heal_20.desc': "Восстанавливает до 20 ОЗ, не выше максимума.",
    'consumable.gold_25.title': "Золотой запас",
    'consumable.gold_25.desc': "25 золота. Выдача отключается до появления экономики.",

    // Stat Boosts
    'upgrade.stat.heal_20.title': 'Зелье здоровья',
    'upgrade.stat.heal_20.desc': 'Восстанавливает 20 ОЗ',
    'upgrade.stat.might_10.title': 'Шпинат',
    'upgrade.stat.might_10.desc': 'Урон +10%',
    'upgrade.stat.haste_10.title': 'Пустой том',
    'upgrade.stat.haste_10.desc': 'Перезарядка -10%',
    'upgrade.stat.speed_10.title': 'Кроссовки',
    'upgrade.stat.speed_10.desc': 'Скор. бега +10%'
};
